import {
  ROOM_TYPE_FILTER,
  ROOM_TYPE_SPECIFIC,
  SHARING_OPTIONS_BATHROOM,
  SHARING_OPTIONS_KITCHEN,
} from "../components/FilterSection/data/filtersList";
import formatFilterState from "../utils/formatFilterState";
import {
  getAllowedFilters,
  getFilterParams,
  getInventoryOgImage,
  getSearchCanonicalUrl,
  getSearchLink,
  getSearchMetaDescription,
  getSearchMetaTitle,
  getSearchRobotsTag,
} from "./helper";
import {
  getSearchFailure,
  getSearchListFailure,
  getSearchListRequesting,
  getSearchListSuccess,
  gettSearchListSuccessPagination,
  updateNearbyData,
  updateTrustpilotReviews,
  // updateFatFooterData,
} from "./searchPageReducer";
import { getMetaSuccess, updateNoindexSlugs } from "@App/appReducer";
import config from "@Config/index";
import { AppThunk } from "@Store/index";
import generateImageSchemaData from "@Utils/generateImageSchemaData";
import getFAQSchema from "@Utils/getFAQSchema";
import axios from "axios";
import {
  COUNTRIES_WITH_MONTHLY_DURATION,
  JSON_SCHEMA_REMOVAL_TEST_PAGES_SLUG,
} from "constants/configConstants";
// import getFatFooterData from "services/getFatFooterData";
import getRegionsBulk from "services/getRegionsBulk";
import { collectSearchSlugs, fetchNoindexSlugs, withoutNoindexed } from "@Utils/noindexLinks";
import { metaTypesHas } from "../components/SearchList/helper";
import urljoin from "helpers/urljoin";
import getCookie from "helpers/getCookie";
import { getTrustpilotData } from "../components/SearchList/components/TrustPilotBanner/helper";
import {
  applySearchGeoRadius,
  getGlobalRequestSourceHeader,
  resolveRadiusExpandEnabled,
} from "../utils/geoRadiusExpand";
import { isAppWebViewFromAppData } from "hooks/useIsApp";

const getInventoriesRequestHeaders = (getState: () => any, filters: any, region: any) => {
  const state = getState();
  return {
    ...(config.IS_CHINA_APP ? {} : { "X-Use-Java-Api": "true" }),
    ...getGlobalRequestSourceHeader({
      filters,
      region,
      enabled: resolveRadiusExpandEnabled(state?.searchPageData?.radiusExpandEnabled),
      isExpanded: Boolean(state?.searchPageData?.isRadiusExpanded),
      isChinaApp: Boolean(config.IS_CHINA_APP),
      isWhitelabel: Boolean(config.IS_WHITELABEL_APP || state?.whitelabelData?.isWhitelabel),
      isApp: isAppWebViewFromAppData(state?.appData),
      isMiniProgram: Boolean(state?.appData?.isMiniProgram),
    }),
  };
};

const fetchInventorisData =
  ({
    canonical_name = "",
    filters = {},
    href = "",
    region,
    page,
    forceFetch = false,
    searchSortKey,
    extraParams = {},
    updateShapedData = null,
    skipScroll = false,
    appendResults = false,
    reqCookies = null, // Add reqCookies parameter for SSR
  }: any): AppThunk =>
  async (dispatch, getState: any) => {
    try {
      const { userLanguage, isInternalUser, userCurrency } = getState().appData;
      const language = userLanguage;
      const sortKey = searchSortKey;
      const radiusFilters = {
        ...(getState()?.searchPageData?.filters || {}),
        ...filters,
      };
      dispatch(getSearchListRequesting({ canonical_name, page, forceFetch, skipScroll }));
      const whiteListedFilters = getAllowedFilters(filters);
      const finalfilters = getFilterParams(filters, "destination_distances");
      delete finalfilters["range_available_price"];

      if (
        (!finalfilters["sort_key"] || finalfilters["sort_key"] === "relevance_sorting_v2") &&
        sortKey !== "relevance_sorting_v2" &&
        !getState()?.whitelabelData?.isWhitelabel
      ) {
        finalfilters["sort_key"] = sortKey;
      }

      // if (finalfilters && finalfilters?.tags?.includes("dual_occupancy")) {
      //   finalfilters.meta_dual_occupancy = true;
      // }

      const isAgentViewOpened = getState()?.searchPageData?.isAgentViewOpened || false;
      const customPropertyName = getState()?.searchPageData?.customPropertyName || "";
      const customPMGName = getState()?.searchPageData?.customPMGName || "";
      const agentViewZone = getState()?.searchPageData?.agentViewZone || 0;
      const agentViewStatuses = getState()?.searchPageData?.agentViewStatuses || "";
      const agentViewWhiteLabelPartnerId = getState()?.searchPageData?.whiteLabelPartnerId || null;
      const customLocation = getState()?.searchPageData?.customLocation || {};
      const customLocationCoords = customLocation?.geometry?.location;
      const destinationLat =
        customLocationCoords?.lat || region?.location?.location_coordinates?.lat;
      const destinationLng =
        customLocationCoords?.lng || region?.location?.location_coordinates?.lng;
      const inventoriesParams = {
        ...whiteListedFilters,
        params: {
          ...(finalfilters || {}),
          ...(isInternalUser ? { isInternalUser: true } : { isInternalUser: false }),
          ...(isInternalUser && { internal_user_monitoring: true }),
          isInternalUser,
          ...(userCurrency ? { user_currency: userCurrency } : {}),
          ...(filters.locality &&
            region.region_type === "establishment" && {
              locality_canonical_name: filters.locality,
              region_canonical_name: canonical_name,
              locality_radius: 10000,
            }),
          ...(region.region_type === "sublocality"
            ? {
                locality_canonical_name: filters.locality || canonical_name,
              }
            : {
                region_canonical_name: filters.university || canonical_name,
              }),
          ...(filters?.range_available_price && {
            ...(COUNTRIES_WITH_MONTHLY_DURATION.includes(region?.location?.country?.short_name)
              ? { range_available_price: filters?.range_available_price }
              : { weekly_range_price: filters?.range_available_price }),
            duration: COUNTRIES_WITH_MONTHLY_DURATION.includes(
              region?.location?.country?.short_name,
            )
              ? "monthly"
              : "weekly",
          }),
          use_open_search: true,
          with_active_rooms: true,
          use_v2_structure: true,
          region_radius:
            filters?.region_radius ||
            (region?.location?.country?.short_name === "AE" ? 60000 : 20000),
          limit: 10,
          ...(page && page > 1 ? { p: page } : {}),
          lang: language || "en",
          destination_coordinates: `${destinationLat},${destinationLng}`,
        },
      };
      applySearchGeoRadius(
        inventoriesParams.params,
        radiusFilters,
        region,
        getState()?.searchPageData?.isRadiusExpanded,
        resolveRadiusExpandEnabled(getState()?.searchPageData?.radiusExpandEnabled),
      );

      if (isAgentViewOpened) {
        inventoriesParams.params.active_children_with_filters = true;
        inventoriesParams.params.methods = `${inventoriesParams.params.methods},is_custom_form_enabled`;
      }
      if (isAgentViewOpened) {
        if (!inventoriesParams.params?.only?.includes("owner")) {
          inventoriesParams.params.only = `${inventoriesParams.params.only},owner,meta,provider_id,payment_types`;
        }
      }

      if (customPropertyName) {
        inventoriesParams.params.name = customPropertyName;
      }
      if (customPMGName) {
        inventoriesParams.params.owner = customPMGName;
      }
      if (agentViewZone) {
        inventoriesParams.params.zone = agentViewZone;
      }
      if (agentViewStatuses) {
        inventoriesParams.params.statuses = agentViewStatuses;
      }
      if (agentViewWhiteLabelPartnerId) {
        inventoriesParams.params.whitelabel_partner_id = agentViewWhiteLabelPartnerId;
      }

      const serializedParams = JSON.stringify(inventoriesParams);
      const parsedParams = JSON.parse(serializedParams);
      const clonedOptions = { ...parsedParams };
      let updatedFilters;
      // handling meta_unit_types filters explicitly as we need to send comma seperated 3 values for student accomodation filter.
      if (parsedParams?.filters) {
        const unitTypes = clonedOptions?.filters?.meta_unit_types;
        updatedFilters = unitTypes?.split(",").includes("student_accommodation")
          ? `${unitTypes},apartment,university_hall`
          : unitTypes;
        clonedOptions.filters.meta_unit_types = updatedFilters;
      }
      const selectedOption = parsedParams?.filters ? clonedOptions : parsedParams;

      if (selectedOption.params?.meta_types) {
        selectedOption.params = {
          ...selectedOption.params,
          room_types: [...ROOM_TYPE_SPECIFIC.list, ...ROOM_TYPE_FILTER.list]
            .flat()
            .map((it) => it.value)
            .filter((it) => metaTypesHas(selectedOption.params?.meta_types, it))
            .join(","),
          bathroom_types: SHARING_OPTIONS_BATHROOM.list
            .map((it) => it.value)
            .filter((it) => metaTypesHas(selectedOption.params?.meta_types, it))
            .join(","),
          kitchen_types: SHARING_OPTIONS_KITCHEN.list
            .map((it) => it.value)
            .filter((it) => metaTypesHas(selectedOption.params?.meta_types, it))
            .join(","),
        };

        delete selectedOption.params?.meta_types;
        if (!selectedOption.params.bathroom_types) {
          delete selectedOption.params.bathroom_types;
        }
        if (!selectedOption.params.kitchen_types) {
          delete selectedOption.params.kitchen_types;
        }
        if (!selectedOption.params.room_types) {
          delete selectedOption.params.room_types;
        }
      }
      const {
        isWhitelabel,
        partner_id,
        isUniversityPartner,
        config: whitelabelConfig,
      } = getState().whitelabelData;

      const isRestrictedSearchEnabled =
        isWhitelabel && !isUniversityPartner && !whitelabelConfig?.is_global_search_enabled;

      if ((isWhitelabel && isUniversityPartner) || isRestrictedSearchEnabled) {
        selectedOption.params.whitelabel_partner_id = partner_id;
      }

      if (
        isWhitelabel &&
        isUniversityPartner &&
        whitelabelConfig?.prioritize_whitelabel_inventories
      ) {
        const housingType = selectedOption.params.housing_type;
        if (!housingType || housingType === "all") {
          selectedOption.params.prioritize_partner_whitelabel_inventories = true;
        }
      }

      let nearbyDataPromise: Promise<any> = Promise.resolve(null);

      if (
        region?.id &&
        region?.location?.location_coordinates?.lat &&
        region?.location?.location_coordinates?.lng &&
        region?.root_name
      ) {
        const filterBase = {
          region_types: "locality,sublocality,establishment",
          combined_name: region?.root_name?.split(" ")?.join("_"),
          not_id: region.id,
          region_point_sort: [
            region.location.location_coordinates.lat,
            region.location.location_coordinates.lng,
          ].join(","),
          id: canonical_name,
          for_search_page: true,
          ...(userLanguage !== "en" && { lang: userLanguage }),
        };

        nearbyDataPromise = getRegionsBulk(filterBase).catch((error) => {
          if (typeof window !== undefined && typeof window.Sentry !== "undefined") {
            window.Sentry.captureException(`Nearby Section Data Fetching Error -> ${error}`);
          }
          return null;
        });
      }

      if (extraParams && Object.keys(extraParams).length > 0) {
        selectedOption.params = { ...selectedOption.params, ...extraParams.params };
        if (extraParams?.only)
          selectedOption.params.only = `${selectedOption?.params.only},${extraParams.only},provider_id,payment_type,payment_types`;
      }

      // Fetch Trustpilot reviews data server-side for SSR
      let trustpilotDataPromise: Promise<any> = Promise.resolve(null);
      if (!__CLIENT__) {
        let tpCity: string | null = null;
        if (region?.region_type === "locality") {
          tpCity = region.name;
        } else if (region?.region_type === "establishment") {
          tpCity = region.parent_name;
        }
        const tpUniversity = region?.region_type === "establishment" ? region.name : null;

        trustpilotDataPromise = getTrustpilotData(tpCity, "British", tpUniversity).then((data) =>
          Array.isArray(data) ? data : [],
        );
      }

      // When locality=near-by-{timestamp}, make a clean nearby API call instead of the normal one
      let isNearbyRequest = false;
      let nearbyCleanParams: any = null;

      // Check for nearby on both client and server side
      // Use includes to match locality=near-by-{timestamp} pattern
      const isNearbyPage =
        (filters?.locality && String(filters.locality).includes("near-by")) ||
        (typeof window !== "undefined" &&
          new URLSearchParams(window.location.search).get("locality")?.includes("near-by"));

      if (isNearbyPage) {
        // Try to get coordinates from cookies (client-side) or from a different source on server
        let nearbyLat: string | null = null;
        let nearbyLng: string | null = null;

        if (typeof window !== "undefined") {
          // Client-side: get from browser cookies
          nearbyLat = getCookie("nearby_lat");
          nearbyLng = getCookie("nearby_lng");
          console.log("[fetchInventoriesData] Client-side cookies", { nearbyLat, nearbyLng });
        } else if (reqCookies) {
          // Server-side: get from request cookies
          nearbyLat = reqCookies.nearby_lat || null;
          nearbyLng = reqCookies.nearby_lng || null;
        } else {
          // On server side without cookies, skip nearby API call
          console.log("[fetchInventoriesData] Server-side render, no cookies available");
        }

        if (nearbyLat && nearbyLng) {
          const lat = parseFloat(nearbyLat);
          const lng = parseFloat(nearbyLng);

          if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
            isNearbyRequest = true;

            // Build filter params from the current filters
            const nearbyFilterParams = { ...finalfilters };
            if (nearbyFilterParams?.locality?.includes("near-by")) {
              delete nearbyFilterParams["locality"];
              if (region?.region_type === "establishment") {
                nearbyFilterParams.region_canonical_name = region?.canonical_name;
              }
            } else {
              // if university selected first and then locality is present in filters, use it
              if (region?.region_type === "establishment") {
                nearbyFilterParams.region_canonical_name = region?.canonical_name;
                if (nearbyFilterParams["locality"]) {
                  nearbyFilterParams.locality_canonical_name = nearbyFilterParams["locality"];
                  nearbyFilterParams.locality_radius = 10000;
                }
              }

              // if locality is selected first, use it
              if (region?.region_type === "sublocality") {
                nearbyFilterParams.locality_canonical_name = region.canonical_name;
                delete nearbyFilterParams.region_canonical_name;
              }
            }

            // Determine if any filters are applied (excluding default filters)
            // const hasActiveFilters = Object.keys(filters).some(
            //   (key) =>
            //     key !== "locality" &&
            //     filters[key] !== undefined &&
            //     filters[key] !== null &&
            //     filters[key] !== "",
            // );

            // Default (landing page): use nearby_sorted_distance with ascending order
            // Override this ONLY if all conditions below are satisfied:
            // 1. Either:
            //    a. Filters locality includes "near-by"
            //    b. OR nearbyFilterParams.locality_canonical_name is present
            // 2. Sort key is NOT "nearby_sorted_distance"
            // 3. AND either:
            //    a. Region type is "establishment" or "sublocality" (always allow custom sort)
            //    b. OR sort key is one of the allowed values: ["distance", "available_price", "created"]

            // Otherwise, fallback to default:
            // sort_key = "nearby_sorted_distance"
            // sort_order = "asc"

            const isValidRegion =
              region?.region_type === "establishment" || region?.region_type === "sublocality";

            const selectedSortKey = finalfilters?.sort_key || sortKey;

            const isCustomSort = selectedSortKey && selectedSortKey !== "nearby_sorted_distance";

            const allowedSortKeys = ["distance", "available_price", "created"];

            const isAllowedSort = allowedSortKeys.includes(selectedSortKey);

            // FINAL LOGIC
            if (
              (filters?.locality?.includes("near-by") ||
                nearbyFilterParams.locality_canonical_name) &&
              isCustomSort &&
              (isValidRegion || isAllowedSort)
            ) {
              // Use custom sort
              nearbyFilterParams.sort_key = selectedSortKey || "relevance_v0";
              nearbyFilterParams.sort_order = finalfilters?.sort_order || "desc";
            } else {
              // Default landing behavior
              nearbyFilterParams.sort_key = "nearby_sorted_distance";
              nearbyFilterParams.sort_order = "asc";
            }

            nearbyCleanParams = {
              isNearbyPage: true,
              lat,
              lng,
              limit: 10,
              use_java_api: true,
              use_v2_structure: true,
              with_active_rooms: true,
              lang: "en",
              only: "id,name,parent_id,pricing,images,videos,location,canonical_name,tags,features,source_link,available,slashed_available_price,nearby_distance_miles,provider_id",
              methods:
                "destination_distances,active_children_count,active_children_with_filters_count,location_coordinates,reviews_count,reviews_rating,meta_short,feature_tags,amber_sales,images_with_featured",
              ...(userCurrency ? { user_currency: userCurrency } : {}),
              ...nearbyFilterParams,
              ...(filters?.range_available_price && {
                ...(COUNTRIES_WITH_MONTHLY_DURATION.includes(region?.location?.country?.short_name)
                  ? { range_available_price: filters?.range_available_price }
                  : { weekly_range_price: filters?.range_available_price }),
                duration: COUNTRIES_WITH_MONTHLY_DURATION.includes(
                  region?.location?.country?.short_name,
                )
                  ? "monthly"
                  : "weekly",
              }),
              region_radius: 30000, // only for near by section
            };

            if (nearbyCleanParams?.meta_types) {
              nearbyCleanParams = {
                ...nearbyCleanParams,
                room_types: [...ROOM_TYPE_SPECIFIC.list, ...ROOM_TYPE_FILTER.list]
                  .flat()
                  .map((it) => it.value)
                  .filter((it) => metaTypesHas(nearbyCleanParams?.meta_types, it))
                  .join(","),
                bathroom_types: SHARING_OPTIONS_BATHROOM.list
                  .map((it) => it.value)
                  .filter((it) => metaTypesHas(nearbyCleanParams?.meta_types, it))
                  .join(","),
                kitchen_types: SHARING_OPTIONS_KITCHEN.list
                  .map((it) => it.value)
                  .filter((it) => metaTypesHas(nearbyCleanParams?.meta_types, it))
                  .join(","),
              };

              delete nearbyCleanParams?.meta_types;
              if (!nearbyCleanParams.bathroom_types) {
                delete nearbyCleanParams.bathroom_types;
              }
              if (!nearbyCleanParams.kitchen_types) {
                delete nearbyCleanParams.kitchen_types;
              }
              if (!nearbyCleanParams.room_types) {
                delete nearbyCleanParams.room_types;
              }
            }

            if (
              region &&
              (region.region_type === "establishment" || region.region_type === "sublocality")
            ) {
              const univlat = parseFloat(region?.location?.location_coordinates?.lat);
              const univlng = parseFloat(region?.location?.location_coordinates?.lng);
              delete nearbyCleanParams.lat;
              delete nearbyCleanParams.lng;
              delete nearbyCleanParams.isNearbyPage;
              nearbyCleanParams.destination_coordinates = `${univlat},${univlng}`;
            }
          }
        }
      }

      applySearchGeoRadius(
        selectedOption.params,
        radiusFilters,
        region,
        getState()?.searchPageData?.isRadiusExpanded,
        resolveRadiusExpandEnabled(getState()?.searchPageData?.radiusExpandEnabled),
      );

      const inventoryDataPromise = isNearbyRequest
        ? axios.get(`${config.API_URL}/api/v1/inventories`, {
            params: nearbyCleanParams,
            withCredentials: true,
            ...config.apiParams,
          })
        : axios.get(`${config.API_URL}/api/v1/inventories`, {
            params: {
              ...selectedOption.params,
              use_java_api: true,
            },
            withCredentials: true,
            ...config.apiParams,
            headers: getInventoriesRequestHeaders(getState, radiusFilters, region),
          });

      const [inventoryResponse, nearbyResponse, trustpilotResponse] = await Promise.all([
        inventoryDataPromise,
        nearbyDataPromise,
        trustpilotDataPromise,
        // fatFooterDataPromise, // Temporarily disabled
      ]);

      const {
        data: { data },
      } = inventoryResponse;

      const totalPages = Math.ceil((data?.meta?.count || 0) / 10);
      if (page && page > 1 && page > totalPages) {
        dispatch(getSearchFailure("404 - Page not found: requested page exceeds available pages"));
        return;
      }

      if (updateShapedData && data) {
        updateShapedData(data);
      }

      const nearbyCities = nearbyResponse?.data?.data?.[0]?.result || [];
      const nearbyAreas = nearbyResponse?.data?.data?.[1]?.result || [];
      const nearbyUniversities = nearbyResponse?.data?.data?.[2]?.result || [];

      // SEO (PLG-3406): which search pages linked from this page (nearby places, quick
      // university links, FAQ / summary / description content) are noindexed.
      const noindexSlugs = await fetchNoindexSlugs(
        collectSearchSlugs(region, nearbyCities, nearbyAreas, nearbyUniversities),
      );
      dispatch(updateNoindexSlugs(noindexSlugs));

      if (nearbyResponse && nearbyResponse.data) {
        dispatch(
          updateNearbyData({
            cities: withoutNoindexed(nearbyCities, noindexSlugs),
            areas: withoutNoindexed(nearbyAreas, noindexSlugs),
            universities: withoutNoindexed(nearbyUniversities, noindexSlugs),
            inventories: nearbyResponse.data?.data?.[3]?.result || [],
          }),
        );
      }

      // Dispatch trustpilot reviews for SSR
      if (
        trustpilotResponse &&
        Array.isArray(trustpilotResponse) &&
        trustpilotResponse.length > 0
      ) {
        dispatch(updateTrustpilotReviews(trustpilotResponse));
      }

      // TODO: Re-enable fat footer data dispatch when needed
      // // Dispatch fat footer data if available
      // if (
      //   fatFooterResponse &&
      //   ((fatFooterResponse.most_booked_properties &&
      //     fatFooterResponse.most_booked_properties.length > 0) ||
      //     (fatFooterResponse.top_rated_inventories &&
      //       fatFooterResponse.top_rated_inventories.length > 0) ||
      //     (fatFooterResponse.newly_added_inventories &&
      //       fatFooterResponse.newly_added_inventories.length > 0))
      // ) {
      //   // Add region_id to the fat footer data for validation
      //   const fatFooterDataWithRegion = {
      //     ...fatFooterResponse,
      //     region_id: regionIdForFatFooter,
      //   };
      //   dispatch(updateFatFooterData(fatFooterDataWithRegion));
      // } else {
      //   console.log("No fat footer data to dispatch for region:", region?.canonical_name);
      //   // Clear fat footer data when no data is available
      //   dispatch(updateFatFooterData({}));
      // }

      if (canonical_name === region?.canonical_name) {
        // Check region type
        if (["locality", "sublocality", "establishment"].includes(region?.region_type)) {
          inventoriesParams.filters.region = region;
          inventoriesParams.filters.region_canonical_name = region?.canonical_name;
        }
      }

      // Meta
      const meta: any = {};
      filters = inventoriesParams.filters;
      if (region.region_type === "establishment") {
        filters.university = region.canonical_name;
      }
      if (region.region_type === "sublocality") {
        filters.locality = region.canonical_name;
      }
      const formatterFilters = formatFilterState(filters);

      meta.url = href;
      meta.canonical_url = getSearchLink(canonical_name, filters);
      meta.canonical_url_suffix = getSearchCanonicalUrl(canonical_name, filters, userLanguage);

      // Check for custom robots tag from region meta
      const customRobotsTag = getSearchRobotsTag(filters);
      if (customRobotsTag) {
        meta.robots = customRobotsTag;
      }

      meta.title_prefix = getSearchMetaTitle(data?.meta, filters, userLanguage) || "";
      meta.description = getSearchMetaDescription(data?.meta, filters, userLanguage);
      meta.keywords = region?.meta?.keywords
        ? region.meta.keywords.split(",").map((keyword: any) => keyword.trim())
        : [];
      meta.showZhDescription = language === "zh";

      // image must be of the first inventory
      meta.ogImage = getInventoryOgImage(data?.result?.[0], "medium") || "";
      const imageSchemaData = generateImageSchemaData(data?.result);
      // FAQ markup must describe content that is actually on the page, and paginated
      // URLs (?p=2 and beyond) no longer render the FAQ section.
      const faqSchemaData =
        (page && page > 1) || JSON_SCHEMA_REMOVAL_TEST_PAGES_SLUG.includes(canonical_name)
          ? []
          : getFAQSchema(region?.faqs);
      const schemaData = [imageSchemaData, faqSchemaData]?.flat();

      // WebPage schema for city pages
      if (region.region_type === "locality") {
        const pageUrl = urljoin(config.WEB_SERVICE_URL, getSearchLink(canonical_name));
        const countryAncestor = (region?.ancestors || region?.ancestors_short || []).find(
          (a: any) => a.region_type === "country",
        );
        const aboutBlock: any = {
          "@type": "City",
          name: region.name,
        };
        if (region?.meta?.same_as) aboutBlock.sameAs = region.meta.same_as;
        const webPageSchema: any = {
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${pageUrl}#webpage`,
          url: pageUrl,
          name: meta.title_prefix,
          about: aboutBlock,
        };
        if (countryAncestor) {
          const countryUrl = urljoin(
            config.WEB_SERVICE_URL,
            getSearchLink(countryAncestor.canonical_name),
          );
          webPageSchema.isPartOf = {
            "@type": "WebPage",
            "@id": `${countryUrl}#webpage`,
            url: countryUrl,
          };
          const containedIn: any = {
            "@type": "Country",
            name: countryAncestor.name,
          };
          if (countryAncestor?.meta?.same_as) containedIn.sameAs = countryAncestor.meta.same_as;
          webPageSchema.about.containedInPlace = containedIn;
        }
        schemaData.push(webPageSchema);
      }

      // WebPage schema for university pages
      if (region.region_type === "establishment") {
        const pageUrl = urljoin(config.WEB_SERVICE_URL, getSearchLink(canonical_name));
        const cityAncestor = (region?.ancestors || region?.ancestors_short || []).find(
          (a: any) => a.region_type === "locality",
        );
        const aboutBlock: any = {
          "@type": "EducationalOrganization",
          name: region.name,
        };
        if (region?.meta?.same_as) aboutBlock.sameAs = region.meta.same_as;
        const webPageSchema: any = {
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${pageUrl}#webpage`,
          url: pageUrl,
          name: `Student Accommodation near ${region.name}`,
          about: aboutBlock,
        };
        if (cityAncestor) {
          const cityUrl = urljoin(
            config.WEB_SERVICE_URL,
            getSearchLink(cityAncestor.canonical_name),
          );
          webPageSchema.isPartOf = {
            "@type": "WebPage",
            "@id": `${cityUrl}#webpage`,
            url: cityUrl,
          };
        }
        schemaData.push(webPageSchema);
      }

      // WebPage schema for sub-locality pages
      if (region.region_type === "sublocality") {
        const pageUrl = urljoin(config.WEB_SERVICE_URL, getSearchLink(canonical_name));
        const cityAncestor = (region?.ancestors || region?.ancestors_short || []).find(
          (a: any) => a.region_type === "locality",
        );
        const cityName = cityAncestor?.name || "";
        const location = region?.location || {};
        const webPageSchema: any = {
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${pageUrl}#webpage`,
          url: pageUrl,
          name: meta.title_prefix,
          about: {
            "@type": "Place",
            name: region.name,
            address: {
              "@type": "PostalAddress",
              streetAddress: location?.route?.long_name || "",
              addressLocality: cityName,
              addressCountry: location?.country?.short_name || "",
            },
            containedInPlace: {
              "@type": "City",
              name: cityName,
            },
          },
        };
        if (cityAncestor) {
          const cityUrl = urljoin(
            config.WEB_SERVICE_URL,
            getSearchLink(cityAncestor.canonical_name),
          );
          webPageSchema.isPartOf = {
            "@type": "WebPage",
            "@id": `${cityUrl}#webpage`,
            url: cityUrl,
          };
        }
        schemaData.push(webPageSchema);
      }

      meta.schemaData = schemaData;

      // Region Inactive
      if (region.state != "active") {
        meta.index = false;
      }

      // Zero-inventories pages to be non-indexed until navigational linking for such pages is turned off
      if (data?.meta?.count == 0) {
        meta.index = false;
      }

      // Paginated search results (?p=2+) should not be indexed but remain crawlable
      if (page && page > 1) {
        meta.index = false;
        meta.robots = "noindex,follow";
      }

      // If nearby page has 0 properties, redirect to fallback page BEFORE dispatching success
      const hasFiltersApplied = Object.keys(filters).some(
        (key) =>
          key !== "locality" &&
          filters[key] !== undefined &&
          filters[key] !== null &&
          filters[key] !== "",
      );

      if (isNearbyRequest && data?.result?.length === 0 && !hasFiltersApplied) {
        window.location.replace("/nearby");
        return;
      }

      dispatch(
        getSearchListSuccess({
          ...data,
          params: isNearbyRequest ? nearbyCleanParams : selectedOption.params,
          filters: formatterFilters,
          region,
          canonical_url: meta.canonical_url,
          canonical_name,
          metaSEO: meta,
          customPropertyName: extraParams?.params?.name || "",
          skipScroll: skipScroll || appendResults,
          appendResults,
        }),
      );

      dispatch(getMetaSuccess(meta));
    } catch (error: any) {
      dispatch(getSearchListFailure(error));
    }
  };

export const fetchMoreInventoriesList =
  ({ params, nextPage, canonical_name }: any): AppThunk =>
  async (dispatch, getState: any) => {
    try {
      dispatch(getSearchListRequesting({ canonical_name, page: nextPage }));

      const { data } = await axios.get(`${config.API_URL}/api/v1/inventories/`, {
        params: {
          ...params,
          p: nextPage,
          use_java_api: true,
        },
        headers: getInventoriesRequestHeaders(
          getState,
          getState()?.searchPageData?.filters,
          getState()?.searchPageData?.region,
        ),
      });
      dispatch(gettSearchListSuccessPagination({ ...data, params }));
    } catch (error: any) {
      dispatch(getSearchListFailure(error));
    }
  };

export default fetchInventorisData;
